import { BalanceData, BossMechanic } from "@highmarch/domain";
import type { Readiness } from "./hub/readiness";
import { icon } from "./iconsKit";
import { btnHtml } from "./kit";
import { portraitUrl } from "./portraits";

/**
 * The defeat screen that teaches (engagement doc §B4): how far the run got against the best on this level, what got
 * through by enemy type, one counter tip for the worst leaker and one readiness line. Built as data first
 * (`defeatReport`, tested) and drawn by `defeatPanelHtml`; screens.ts puts it where the victory stats sit.
 */

/** What a leaker is, for the counter table: read from its archetype data, so a new enemy lands in the right row. */
export type CounterKind = "air" | "boss" | "healer" | "armoured" | "fast" | "swarm";

/** At or above this speed (cells/s) an enemy counts as fast: the Runner (2.0) is, the Flyer (1.2) is not. */
const FAST_CELLS_PER_SECOND = 1.5;
/** Leak portraits on the defeat card: the worst few, so the row fits a phone's width. */
const MAX_LEAK_FACES = 4;

/** The counter table: towers to suggest (tower ids, best first) and why, per kind of leaker. */
export const COUNTER_TIPS: Readonly<Record<CounterKind, { readonly towers: readonly string[]; readonly why: string }>> = {
  air: { towers: ["ballista", "archer", "lightning", "arcane"], why: "flyers pass over Cannon, Mortar, Fire and Poison" },
  boss: { towers: ["frost", "hex", "ballista", "fire"], why: "slow or curse the boss where your strongest towers overlap" },
  healer: { towers: ["hex", "lightning", "cannon"], why: "a Hex halves its healing; splash and chains hit the pack it keeps alive" },
  armoured: { towers: ["poison", "arcane", "lightning"], why: "armour blunts arrows and shot; Poison shreds it, Arcane and Lightning ignore it" },
  fast: { towers: ["frost", "barracks"], why: "slow them, or block the road with soldiers, inside your towers' reach" },
  swarm: { towers: ["cannon", "mortar", "fire"], why: "splash and burn thin a crowd" },
};

export function counterKind(balance: BalanceData, enemyIndex: number): CounterKind {
  const e = balance.enemies[enemyIndex];
  if (e.isAir) return "air";
  if (e.bossMechanic !== BossMechanic.None) return "boss";
  if (e.healRadius.isPositive()) return "healer";
  if (e.armour.isPositive() || e.shieldRadius.isPositive()) return "armoured";
  if (e.speedCellsPerSecond.toNumber() >= FAST_CELLS_PER_SECOND) return "fast";
  return "swarm";
}

export interface CounterTip { readonly enemy: string; readonly kind: CounterKind; readonly text: string }

/**
 * The tip for one leaker: the table's towers the player owns (up to two), or — when they own none of them — the first
 * one with the level it unlocks at, so the tip never asks for something they cannot build without saying so.
 */
export function counterTip(balance: BalanceData, enemyIndex: number, owned: readonly boolean[]): CounterTip {
  const kind = counterKind(balance, enemyIndex);
  const row = COUNTER_TIPS[kind];
  const towers = row.towers.map((id) => balance.towers.find((t) => t.id === id)).filter((t) => t !== undefined);
  const have = towers.filter((t) => owned[t.index]).slice(0, 2).map((t) => t.name);
  const first = towers[0];
  const name = balance.enemies[enemyIndex].name;
  const what = have.length > 0 ? have.join(" or ") : first ? `${first.name} once it joins you at level ${first.unlockLevel}` : "more towers";
  return { enemy: name, kind, text: `Try ${what}: ${row.why}.` };
}

/** The leaker with the most leaks (ties: the one that hurt the base more); −1 when nothing leaked. */
export function topLeaker(balance: BalanceData, leaks: readonly number[]): number {
  let best = -1;
  leaks.forEach((n, i) => {
    if (n <= 0) return;
    if (best < 0 || n > leaks[best] || (n === leaks[best] && balance.enemies[i].leakDamage > balance.enemies[best].leakDamage)) best = i;
  });
  return best;
}

/** One line from the hub's readiness model for this level: are you ready, and if not, the first thing to do about it. */
export function readinessLine(r: Readiness): string {
  const act = r.actions[0]?.label;
  switch (r.state) {
    case "ready": return "You're strong enough for this one. Try another road or tower mix.";
    case "equip": return act ? `Almost ready: ${act}.` : "Almost ready: stronger gear would tip it.";
    case "underGeared": return act ? `Under-geared here: ${act}.` : "Under-geared here: this level needs stronger gear.";
    default: return r.shortfall === "towers" || r.shortfall === "both" ? `Towers Lv ${r.towers.have} of ${r.towers.target}${act ? `: ${act}` : ""}.` : "No gear needed yet: try another road or tower mix.";
  }
}

export interface DefeatReport {
  /** The wave the run was on when the base fell, of `stages`. */
  readonly reached: number;
  readonly stages: number;
  /** The best wave reached on this level before this run (0: first try). */
  readonly previousBest: number;
  /** Leaks by enemy archetype, worst first. */
  readonly leaks: readonly { readonly index: number; readonly count: number }[];
  readonly tip: CounterTip | null;
  readonly readiness: string | null;
  /** The wave a free checkpoint retry would start on (DESIGN §4.8.8, app `retryCheckpoint`), 0 for none. */
  readonly checkpoint: number;
  /** MP-06: the run carries a War Banner, and the retries it has left with it (the one free retry included). */
  readonly banner?: { readonly left: number };
  /** M36: the rare that broke through the gate and took the fortress (its name), or null. */
  readonly rareEscaped: string | null;
}

export function defeatReport(balance: BalanceData, o: { reached: number; stages: number; previousBest: number; leaksByType: readonly number[]; owned: readonly boolean[]; readiness: Readiness | null; checkpoint?: number; rareEscaped?: string | null; banner?: { readonly left: number } }): DefeatReport {
  const leaks = o.leaksByType.map((count, index) => ({ index, count })).filter((l) => l.count > 0)
    .sort((a, b) => b.count - a.count || balance.enemies[b.index].leakDamage - balance.enemies[a.index].leakDamage);
  const top = topLeaker(balance, o.leaksByType);
  return { reached: o.reached, stages: o.stages, previousBest: o.previousBest, leaks, tip: top >= 0 ? counterTip(balance, top, o.owned) : null, readiness: o.readiness ? readinessLine(o.readiness) : null, checkpoint: o.checkpoint ?? 0, rareEscaped: o.rareEscaped ?? null, ...(o.banner ? { banner: o.banner } : {}) };
}

export function defeatPanelHtml(balance: BalanceData, d: DefeatReport): string {
  const best = d.previousBest <= 0 ? "" : d.reached > d.previousBest ? `<span class="newbest disp">NEW BEST</span>` : `<small>Your best: wave ${d.previousBest}</small>`;
  const faces = d.leaks.slice(0, MAX_LEAK_FACES).map((l) => {
    const e = balance.enemies[l.index];
    const url = portraitUrl(e.id);
    return `<span class="lk" title="${e.name}">${url ? `<img decoding="sync" src="${url}" alt="">` : icon("skull", 18)}${l.count > 1 ? `<i>×${l.count}</i>` : ""}</span>`;
  }).join("");
  return `<div class="defeat">
    <div class="dreach"><b class="disp">Reached wave ${d.reached} of ${d.stages}</b>${best}</div>
    ${faces ? `<div class="dleaks"><small>Got through</small>${faces}</div>` : ""}
    ${lessonHtml(d)}
    ${checkpointHtml(d.checkpoint, d.banner)}
  </div>`;
}

/**
 * The leak tip and the readiness line. Beside a checkpoint retry the card is at its tallest (a 375 × 667 phone, or
 * Larger text, pushed HUB off the screen), so the two fold into one box: the readiness line rides under the tip.
 */
function lessonHtml(d: DefeatReport): string {
  // M36: a rare that reaches the gate takes the whole fortress — that, not the worst leaker, is why the level fell
  const tip = d.rareEscaped ? `<b>The ${d.rareEscaped}</b> broke through and took the fortress with it. Turn every tower on a rare the moment it shows.`
    : d.tip ? `<b>${d.tip.enemy}</b> leaked most. ${d.tip.text}` : "";
  if (d.checkpoint > 0 && tip && d.readiness) {
    return `<p class="dtip folded">${icon("target", 16)}<span>${tip}<small class="rd">${icon("shield", 13)} ${d.readiness}</small></span></p>`;
  }
  return `${tip ? `<p class="dtip">${icon("target", 16)}<span>${tip}</span></p>` : ""}
    ${d.readiness ? `<p class="dready">${icon("shield", 16)}<span>${d.readiness}</span></p>` : ""}`;
}

/**
 * The checkpoint retry (D5): free, once per attempt, never sold — so the button says so. It is a `data-c` choice like
 * RETRY: the end screen hands "checkpoint" to the level flow, which rebuilds the run from its mark by replay. A run
 * carrying a War Banner (MP-06) has one more: the sub-line names the banner and how many are left (never a price).
 */
export function checkpointHtml(wave: number, banner?: { readonly left: number }): string {
  if (wave <= 0) return "";
  const sub = banner ? `War Banner · ${banner.left > 1 ? `${banner.left} retries left` : "last retry"}` : "Free · once per battle";
  return btnHtml(`RETRY FROM WAVE ${wave}`, "blue", { icon: "flag", data: `data-c="checkpoint"`, className: `disp dretry${banner ? " warbanner" : ""}`, sub });
}
